import { Component, type ReactNode } from 'react';

import { reportError } from '@/utils/sentry';
import CrashFallback from './CrashFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  hasError: boolean;
  /** Sentry event id of the crash, so the user's report can be linked to it. */
  eventId: string | null;
}

/**
 * Catches render-time errors, reports them to Sentry, and shows a minimal
 * fallback so a single broken screen doesn't take down the whole app.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, eventId: null };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    this.setState({ eventId: reportError(error, { componentStack: info.componentStack }) || null });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <CrashFallback eventId={this.state.eventId} />;
    }
    return this.props.children;
  }
}
